<template>
  <div class="report-table">
    <report-forms-and-filters v-if="!loading" :reportData="parsedReportData"></report-forms-and-filters>
    <template v-if="generating">
      <div class="generating-msg">
        <vue-spinner></vue-spinner>
        <small>Generating full report, please wait...</small>
      </div>
    </template>

    <div class="vgt-responsive" :class="{'loading': loading}">
      <vue-spinner style="margin: 0 auto"></vue-spinner>
      <table class="vgt-table bordered">
        <thead>
        <tr>
          <th v-if="columns && columns.length">#</th>
          <th v-for="col in columns" :class="{'sorting': !generating && !report.isPreview}">
            <span @click="onSortChange(col.field)">{{ col.label }}</span>
          </th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="row, rowIdx in rows">
          <td>{{ rowIdx + resultsOffset + 1 }}</td>
          <template v-for="(td, tdIdx) in row">
            <td v-if="!isReportColumnHidden(tdIdx)">
              <div v-if="specialReportColumnTypeComponents[getFieldTypeFromFieldName(tdIdx)]">
                <component :is="specialReportColumnTypeComponents[getFieldTypeFromFieldName(tdIdx)]"
                           :data="{td, form_data: rowsFormData[rowIdx][getFormIdFromReportRowKey(tdIdx)]}"
                           :settings="columnSettings[tdIdx.split('_')[1]]"
                />
              </div>
              <span class="td-file-download" v-else-if="filesColumns.includes(tdIdx)" v-html="makeLink(tdIdx, td)">{{td}}</span>
              <template v-else-if="datesColumns.includes(tdIdx)">{{ td | formatDateModalNoTimeZone }}</template>
              <template v-else-if="dateTimeColumns.includes(tdIdx)">{{ dateTimeColumnFormat(td) }}</template>
              <template v-else>{{ td }}</template>
            </td>
          </template>
        </tr>
        </tbody>
      </table>
    </div>

    <div class="vgt-wrap__footer vgt-clearfix" v-if="Object.keys(report).length">
      <template v-if="generating">
        Generating full report, please wait...
        <vue-spinner></vue-spinner>
      </template>
      <template v-if="!generating && !report.isPreview">
        <div class="footer__row-count vgt-pull-left"><span class="footer__row-count__label">Rows per page</span>
          <select autocomplete="off" name="perPageSelect" class="footer__row-count__select" @change="onPerPageChange">
            <option :selected="resultsPerPage == 10" value="10">10</option>
            <option :selected="resultsPerPage == 25" value="25">25</option>
            <option :selected="resultsPerPage == 50" value="50">50</option>
            <option :selected="resultsPerPage == 100" value="100">100</option>
            <option :selected="resultsPerPage == 250" value="250">250</option>
            <option :selected="resultsPerPage == 500" value="500">500</option>
            <option :selected="resultsPerPage == 0" value="0">All</option>
          </select>
        </div>
        <div class="footer__navigation vgt-pull-right">
          <a @click="prevPage()" tabindex="0" class="footer__navigation__page-btn" :class="{ 'disabled': resultsOffset == 0 }">
            <span class="chevron left"></span><span>Prev</span>
          </a>
          <div class="footer__navigation__info">{{( report.count ? resultsOffset + 1 : 0)}} - {{visibleRowsCount}} of {{ report.count }}</div>
          <a @click="nextPage()" tabindex="0" class="footer__navigation__page-btn" :class="{ 'disabled': (resultsOffset + 1 + resultsPerPage > report.count) || (report.count <= resultsLimit) || resultsLimit === 0 }"><span>Next</span>
            <span class="chevron right"></span>
          </a>
        </div>
      </template>
      <template v-if="report.isPreview && !generating">Report Preview only displays top results. Click Finish to generate report.</template>
    </div>
  </div>
</template>

<script>
  import Spinner from '@/components/Spinner';
  import ReportFormsAndFilters from "@/components/ReportFormsAndFilters";
  import {URL} from '@/http-common';
  import specialReportColumnTypeComponents from "../../utils/specialReportColumnTypeComponents";
  import { isReportColumnHidden } from "../../utils/hiddenReportColumns";
  import { getFormIdFromReportRowKey, getFieldTypeFromFieldName } from "../../utils/functions";

  export default {
    props: ['report', 'totalRows', 'loading', 'generating'],
    components: {
      'vue-spinner': Spinner,
      ReportFormsAndFilters
    },
    data() {
      return {
        resultsOffset: 0,
        resultsLimit: 10,
        resultsPerPage: 10,
        sortBy: null,
        sortDir: null,
        filesColumns: [],
        datesColumns: [],
        dateTimeColumns: []
      }
    },
    computed: {
      rowsFormData(){
        return this.rows.map(row => {
          let rowData = {};
          for (const rowKey in row) {
            if (rowKey.includes('forms_data')){
              rowData[getFormIdFromReportRowKey(rowKey)] = row[rowKey]
            }
          }
          return rowData;
        })
      },
      specialReportColumnTypeComponents(){
        return specialReportColumnTypeComponents
      },
      showLoader() {
        return this.loading;
      },
      columns() {
        let columns = this.report.columns;
        _.each(columns, (column, idx) => {
          if(column.field.includes('_file-')) {
            this.filesColumns.push(column.field);
          }
          if(column.field.includes('_date-')) {
            this.datesColumns.push(column.field);
          }
          if(column.field.includes('_referral_timestamp')) {
            this.dateTimeColumns.push(column.field);
          }
        });
        return this.report.columns;
      },
      rows() {
        return this.report.results;
      },
      isPreview() {
        return this.report.isPreview;
      },
      visibleRowsCount() {
        if (this.report.count < this.resultsPerPage) {
          return this.report.count;
        }

        if (this.resultsPerPage === 0) {
          return this.report.count;
        }

        if (this.resultsPerPage + this.resultsOffset > this.report.count) {
          return this.report.count;
        }

        return (this.resultsPerPage + this.resultsOffset)
      },
      parsedReportData() {
        let parsedJson
        try {
          parsedJson = JSON.parse(this.report.data)
        } catch (e) {
          return []
        }
        return parsedJson
      },
      columnSettings() {
        const results = {};

        this.parsedReportData.forEach(form => {
          form.fields.forEach(field => {
            results[field.field] = field;
          })
        })

        return results;
      },
    },
    methods: {
      getFieldTypeFromFieldName(reportRowKey){
        return getFieldTypeFromFieldName(reportRowKey)
      },
      getFormIdFromReportRowKey(reportRowKey){
        return getFormIdFromReportRowKey(reportRowKey)
      },
      isReportColumnHidden( columnValueKey ){
        return isReportColumnHidden(columnValueKey)
      },
      onPageChange() {
        if (this.isPreview || this.generating) {
          return;
        }
        this.resultsLimit = this.resultsPerPage;
        this.$emit('refresh-report', this.resultsOffset, this.resultsLimit, this.sortBy, this.sortDir);
      },
      onPerPageChange(event) {
        if (this.isPreview || this.generating) {
          return;
        }
        let value = event.target.value;
        this.resultsPerPage = parseInt(value);
        this.resultsOffset = 0;
        this.resultsLimit = parseInt(value);
        this.$emit('refresh-report', this.resultsOffset, this.resultsLimit, this.sortBy, this.sortDir);
      },
      onSortChange(field) {
        if (this.isPreview || this.generating) {
          return;
        }
        this.resultsOffset = 0;
        if (field == this.sortBy) {
          this.sortDir = this.sortDir == 'DESC' ? 'ASC' : 'DESC';
        } else {
          this.sortDir = 'ASC';
        }

        this.sortBy = field;

        this.$emit('refresh-report', this.resultsOffset, this.resultsLimit, this.sortBy, this.sortDir);
      },
      prevPage() {
        if (this.isPreview || this.generating || this.resultsOffset === 0) {
          return;
        }
        if (this.resultsOffset < 0) {
          return;
        }
        this.resultsOffset = this.resultsOffset - this.resultsPerPage;
        this.onPageChange();
      },
      nextPage() {
        if (this.isPreview
          || this.generating
          || this.resultsOffset + 1 + this.resultsPerPage > this.report.count
          || this.report.count <= this.resultsLimit
          || this.resultsLimit === 0) {
          return;
        }
        if (this.resultsOffset > this.report.count) {
          return;
        }
        this.resultsOffset = this.resultsOffset + this.resultsPerPage;
        this.onPageChange();
      },
      makeLink(tdIdx, url) {
        if(!url) {
          return '';
        }

        let prefix = tdIdx.split('-')[1]

        let files = JSON.parse(url);

        if(!files || !files.length) {
          return '';
        }

        let fstring = '';

        _.each(files, function(file, idx) {
          let separator = '';
          if(idx+1 < files.length) {
            separator = '<br/>';
          }
          fstring = fstring+'<a href="'+URL+'/form/download/file/'+prefix+'_'+file['file']+'?token='+localStorage.getItem('id_token')+'">'+file['name']+'</a>'+separator;
        });

        return fstring;
      },
      dateTimeColumnFormat(td) {
        if(!td.length) {
          return '';
        }

        let date = td.substring(
          td.indexOf("|") + 1,
          td.lastIndexOf("|")
        );

        date = this.$options.filters.formatDateModalNoTimeZone(date);

        let person = td.substring(0, td.indexOf('|'));
        let time = td.substring(td.lastIndexOf("|")+1);

        return person + ' ' + date + ' ' + time;
      }
    },
  }
</script>

<style lang="scss" scoped>
  .vgt-responsive {
    position: relative;
    .spinner {
      position: absolute;
      top: 50px;
      z-index: 9999;
      left: 0;
      right: 0;
      display: none;
    }
  }

  .vgt-responsive.loading {
    table {
      opacity: 0.5;
    }

    .spinner {
      display: block;
    }
  }

  .report-table {
    position: relative;
  }

  .generating-msg {
    position: absolute;
    top: 10px;
    right: 5px;

    .spinner {
      float: left;
    }
  }
</style>



// WEBPACK FOOTER //
// ReportTable.vue?1f45f421